import { SessionRow } from "../DeclarationCourse/SessionList";
import { DepositInfo } from "../DeclarationV2/types";

/**
 * Brouillons v2 (maquettes 1g, 1h, 2g, 2h du 29 octobre 2026).
 *
 * Un brouillon n'est pas enregistré : c'est un cours du mois précédent,
 * renvoyé par l'API `teachr_get_course_previous_month` (action Redux
 * `getNextCourse`, clé `punctualCourse.nextCourse`), proposé à nouveau pour le
 * mois en cours avec ses dates décalées d'un mois. Ces types décrivent la
 * forme normalisée utilisée par la liste et la page de modification ; la
 * logique métier (décalage, contrôles, envoi) est copiée de
 * `App/Courses/CourseRowActions.tsx` sans changement.
 */

/** Élève (ou client) d'un groupe de brouillons, tel que la liste le présente. */
export interface DraftStudent {
    clientId: number;
    childId: number;
    firstName: string;
    lastName: string;
    /** Identifiant d'adresse du cours source (scénario de déplacement). */
    addressId: number | null;
    /** Date de validation du compte d'avance immédiate (group.startAt), si connue. */
    activationDate: string | null;
    /** Code postal de l'adresse par défaut (TVA du territoire, coopérative). */
    zip: string | null;
}

/** Brouillon normalisé : un cours du mois précédent, déjà décalé d'un mois. */
export interface Draft {
    /** Identifiant du cours source (mois précédent) : clé du brouillon. */
    id: number;
    student: DraftStudent;
    subjectId: number | null;
    subjectLabel: string;
    /** Plusieurs séances (cours du mois) ou un seul cours. */
    multiple: boolean;
    /** Séances décalées d'un mois (jour de semaine conservé) ; une seule ligne pour un cours ponctuel. */
    sessions: SessionRow[];
    /**
     * Ancien cours « Plusieurs cours » sans détail des séances : aucune séance
     * n'est reprise, le prof choisit ses jours dans le calendrier.
     */
    needsDates: boolean;
    /** Heure de début du cours du mois dernier (« HH:mm »), proposée pour les nouveaux jours. */
    previousStartTime: string | null;
    /**
     * Prix horaire payé par le client, avance immédiate déduite (« 25 »).
     * Relation Teach'r : le tarif horaire du prof (`teachrPrice`), tel quel.
     */
    price: string;
    deposit: DepositInfo;
    sessionDescriptionOverride: string | null;
    isTeachrContact: boolean;
    /** Tarif fixé par la plateforme (custom_fees sans contact Teach'r) : voir `relationTeachr.ts`. */
    isRelationTeachr: boolean;
    /** Compteurs du mois en cours pour l'élève, tels que renvoyés sur le cours. */
    coursesThisRollingMonth: number | null;
    coursesMonthByThisTeachr: number | null;
    /** Montant déjà déclaré ce mois pour l'élève (brut, comme `cours.amount`). */
    amountDeclaredThisMonth: number;
    /** Nombre de séances minimum pour ce brouillon (un ancien mensuel à une séance reste déclarable tel quel). */
    minSessions: number;
    /** Objet brut de l'API, conservé pour l'envoi (address_id, subjectAsked_id, …). */
    raw: any;
}

/** Groupe de brouillons d'un même élève (maquette 1g). */
export interface DraftGroup {
    key: string;
    student: DraftStudent;
    drafts: Draft[];
}

/** Modifications apportées à un brouillon sur la page de modification. */
export interface DraftEdits {
    sessions: SessionRow[];
    price: string;
    deposit: DepositInfo;
    sessionDescriptionOverride: string | null;
}

export const editsFromDraft = (draft: Draft): DraftEdits => ({
    sessions: draft.sessions,
    price: draft.price,
    deposit: draft.deposit,
    sessionDescriptionOverride: draft.sessionDescriptionOverride,
});

export const draftWithEdits = (draft: Draft, edits: DraftEdits): Draft => ({ ...draft, ...edits });
